import { Suspense, lazy, useEffect, type ComponentType } from "react";
import { Switch, Route, Router as WouterRouter, useLocation } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { setAuthTokenGetter, setBaseUrl } from "@workspace/api-client-react";
import NotFound from "@/pages/not-found";
import AppLayout from "@/components/layout/app-layout";

const LoginPage = lazy(() => import("@/pages/login"));
const DashboardPage = lazy(() => import("@/pages/dashboard"));
const BancasPage = lazy(() => import("@/pages/bancas"));
const ConcursosPage = lazy(() => import("@/pages/concursos"));
const DisciplinasPage = lazy(() => import("@/pages/disciplinas"));
const ConteudosPage = lazy(() => import("@/pages/conteudos"));
const BlocosEditorPage = lazy(() => import("@/pages/blocos-editor"));
const QuestoesPage = lazy(() => import("@/pages/questoes"));
const SimuladosPage = lazy(() => import("@/pages/simulados"));
const FlashcardsPage = lazy(() => import("@/pages/flashcards"));
const VideosPage = lazy(() => import("@/pages/videos"));
const SolicitacoesPage = lazy(() => import("@/pages/solicitacoes"));
const UsuariosPage = lazy(() => import("@/pages/usuarios"));
const ConfiguracoesPage = lazy(() => import("@/pages/configuracoes"));
const PlanosPage = lazy(() => import("@/pages/planos"));
function AssinaturasRedirect() { const [, setLocation] = useLocation(); useEffect(() => { setLocation("/usuarios"); }, [setLocation]); return null; }
const CuponsPage = lazy(() => import("@/pages/cupons"));
const FinanceiroPage = lazy(() => import("@/pages/financeiro"));
const ImportacaoPage = lazy(() => import("@/pages/importacao"));
const RelatosPage = lazy(() => import("@/pages/relatos"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

// In development, keep requests relative so every hostname (localhost, LAN IP,
// or a custom local name) reaches the same API through Vite's /api proxy.
// Production may still provide an explicit absolute API URL.
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, "")
  || (import.meta.env.PROD ? "https://flashfarma-api-production.up.railway.app" : null);

setAuthTokenGetter(() => localStorage.getItem("ff_admin_token"));
setBaseUrl(API_BASE_URL);

function PageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background text-muted-foreground">
      Carregando...
    </div>
  );
}

function ProtectedRoute({ component: Component }: { component: ComponentType }) {
  const [, setLocation] = useLocation();
  const token = localStorage.getItem("ff_admin_token");

  useEffect(() => {
    if (!token) {
      setLocation("/login");
    }
  }, [token, setLocation]);

  if (!token) return null;

  return (
    <AppLayout>
      <Component />
    </AppLayout>
  );
}

function RootRedirect() {
  const [, setLocation] = useLocation();
  useEffect(() => { setLocation("/dashboard"); }, [setLocation]);
  return null;
}

function Router() {
  return (
    <Switch>
      <Route path="/login" component={LoginPage} />
      <Route path="/" component={RootRedirect} />
      <Route path="/dashboard"><ProtectedRoute component={DashboardPage} /></Route>
      <Route path="/bancas"><ProtectedRoute component={BancasPage} /></Route>
      <Route path="/concursos"><ProtectedRoute component={ConcursosPage} /></Route>
      <Route path="/disciplinas"><ProtectedRoute component={DisciplinasPage} /></Route>
      <Route path="/conteudos"><ProtectedRoute component={ConteudosPage} /></Route>
      <Route path="/conteudos/:id/blocos/:modulo"><ProtectedRoute component={BlocosEditorPage} /></Route>
        <Route path="/questoes"><ProtectedRoute component={QuestoesPage} /></Route>
        <Route path="/simulados"><ProtectedRoute component={SimuladosPage} /></Route>
      <Route path="/flashcards"><ProtectedRoute component={FlashcardsPage} /></Route>
      <Route path="/videos"><ProtectedRoute component={VideosPage} /></Route>
      <Route path="/solicitacoes"><ProtectedRoute component={SolicitacoesPage} /></Route>
      <Route path="/usuarios"><ProtectedRoute component={UsuariosPage} /></Route>
      <Route path="/configuracoes"><ProtectedRoute component={ConfiguracoesPage} /></Route>
      <Route path="/planos"><ProtectedRoute component={PlanosPage} /></Route>
      <Route path="/assinaturas"><ProtectedRoute component={AssinaturasRedirect} /></Route>
      <Route path="/cupons"><ProtectedRoute component={CuponsPage} /></Route>
      <Route path="/financeiro"><ProtectedRoute component={FinanceiroPage} /></Route>
      <Route path="/importacao"><ProtectedRoute component={ImportacaoPage} /></Route>
      <Route path="/relatos"><ProtectedRoute component={RelatosPage} /></Route>
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
          <Suspense fallback={<PageLoader />}>
            <Router />
          </Suspense>
        </WouterRouter>
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
